/**
 * ASCII Hero Engine - AsciiIllustration Class
 * Standalone, reusable class for rotating vector doodles (e.g. star.svg).
 * - Default state: Constantly rotating solid color illustration.
 * - On Hover Spotlight: Triggers an authentic stop-motion floating dance,
 *   frame-quantized stepping rotation, tactile micro-jitter, and reveals
 *   pixel-perfect ASCII typography clipped directly to the rotating SVG path.
 */

import { Easings } from "../utils/easings.js";
import { parseColorToRgb } from "../utils/math.js";
import { STAR_SVG_PATH } from "../constants.js";

export class AsciiIllustration {
  constructor(options = {}) {
    this.name = options.name || "illustration";
    this.type = options.type || "illustration";

    // Positioning: 'portrait' (anchored to subject layout) or 'screen' (anchored to viewport 0..1)
    this.anchor = options.anchor || "portrait";
    this.u = options.u !== undefined ? options.u : 0.58;
    this.v = options.v !== undefined ? options.v : 0.42;
    this.normalized = options.normalized !== undefined ? options.normalized : true;
    this.x = options.x || 0;
    this.y = options.y || 0;
    this.worldX = 0;
    this.worldY = 0;

    // Render dimensions & SVG path definition
    this.size = options.size || 135;
    this.viewBoxWidth = options.viewBoxWidth || 334;
    this.viewBoxHeight = options.viewBoxHeight || 329;
    this.pathData = options.pathData || STAR_SVG_PATH;
    this.path2d = new Path2D(this.pathData);

    // Colors
    this.color = options.color || "#FF7120";
    this.colorRGB = parseColorToRgb(this.color);
    this.asciiColor = options.asciiColor || "#FF7120";
    this.asciiColorRGB = parseColorToRgb(this.asciiColor);
    this.opacity = options.opacity !== undefined ? options.opacity : 1.0;

    // Continuous base rotation
    this.rotation = options.initialRotation || 0;
    this.rotationSpeed = options.rotationSpeed !== undefined ? options.rotationSpeed : 0.004;

    // On-Hover Stop-Motion & Floating Kinematics
    this.hoverFloating = options.hoverFloating !== undefined ? options.hoverFloating : true;
    this.hoverStopMotion = options.hoverStopMotion !== undefined ? options.hoverStopMotion : true;
    this.hoverFps = options.hoverFps || 8; // Stop-motion frame rate on hover (fps)
    this.hoverFloatAmplitude = options.hoverFloatAmplitude !== undefined ? options.hoverFloatAmplitude : 10; // Vertical float range in px
    this.hoverJitter = options.hoverJitter !== undefined ? options.hoverJitter : 2.2; // Tactile paper/cutout jitter in px
    this.hoverRotationSpeed = options.hoverRotationSpeed !== undefined ? options.hoverRotationSpeed : 0.024; // Extra stepped rotation speed on hover
    this.hoverScale = options.hoverScale !== undefined ? options.hoverScale : 1.06; // Scale pulse factor on hover

    // Stop-motion state tracking
    this.stepTimer = 0;
    this.stepCount = Math.floor(Math.random() * 100);
    this.renderFloatX = 0;
    this.renderFloatY = 0;
    this.renderRotation = this.rotation;
    this.renderJitterX = 0;
    this.renderJitterY = 0;
    this.renderJitterRot = 0;

    // 3D Parallax Depth
    this.depth = options.depth !== undefined ? options.depth : 0.85;
    this.layer = options.layer || "foreground"; // 'foreground' or 'background'

    // Typography settings for ASCII reveal
    this.fontSize = options.fontSize || 38;
    this.charWidth = options.charWidth || options.charSpacing || 26;
    this.lineHeight = options.lineHeight || options.charSpacing || 38;
    this.charSpacing = this.charWidth; // Alias for backwards compatibility
    this.fontFamily = options.fontFamily || "Doto, DM Mono, monospace";
    this.fontWeight = options.fontWeight || "900";
    this.asciiChars = options.asciiChars || ["*", "#", "+", "X", "%", "0", "1", "<", ">", "$", "@", "§", "O", "~"];

    // Spotlight detection & easing state
    this.spotlightIntensity = 0;
    this.spotlightRadius = options.spotlightRadius || 260;
    this.spotlightAttackSpeed = options.spotlightAttackSpeed || 0.16;
    this.spotlightDecaySpeed = options.spotlightDecaySpeed || 0.048;

    this.tick = Math.floor(Math.random() * 100);
  }

  update(mouse, layout, config, parallaxX = 0, parallaxY = 0) {
    if (this.targetOpacity !== undefined && this.opacity !== this.targetOpacity) {
      this.opacity += (this.targetOpacity - this.opacity) * (this.fadeSpeed || 0.08);
      if (Math.abs(this.opacity - this.targetOpacity) < 0.005) {
        this.opacity = this.targetOpacity;
      }
    }

    const screenW = (config && config.width) || (typeof window !== "undefined" ? window.innerWidth : 1920);
    const screenH = (config && config.height) || (typeof window !== "undefined" ? window.innerHeight : 1080);

    // Compute anchor position in canvas world coordinates
    if (this.anchor === "screen") {
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    } else if (this.normalized && layout && layout.renderW > 0) {
      this.worldX = layout.posX + this.u * layout.renderW;
      this.worldY = layout.posY + this.v * layout.renderH;
    } else if (this.x !== 0 || this.y !== 0) {
      this.worldX = this.x;
      this.worldY = this.y;
    } else {
      // Fallback to screen coordinates if layout is not yet computed
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    }

    // Effective screen position with 3D parallax
    const drawX = this.worldX + parallaxX * this.depth;
    const drawY = this.worldY + parallaxY * this.depth;

    // Distance from illustration center to spotlight cursor
    const dx = drawX - (mouse ? mouse.x : -9999);
    const dy = drawY - (mouse ? mouse.y : -9999);
    const dist = mouse && mouse.x !== -9999 ? Math.sqrt(dx * dx + dy * dy) : 99999;

    const sRadius = this.spotlightRadius || (config && config.spotlightRadius) || 260;
    const sPower = config && config.spotlightPower !== undefined ? config.spotlightPower : 1.0;

    let targetSpotlight = 0;
    if (mouse && mouse.x !== -9999 && dist < sRadius) {
      const normDist = dist / sRadius;
      targetSpotlight = Easings.easeOutQuart(1 - normDist) * sPower;
    }

    // Asymmetric attack / decay transition
    if (targetSpotlight > this.spotlightIntensity) {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * this.spotlightAttackSpeed;
    } else {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * this.spotlightDecaySpeed;
    }

    // -------------------------------------------------------------
    // ON-HOVER STOP-MOTION FLOATING & STEPPING KINEMATICS
    // -------------------------------------------------------------
    const stepInterval = Math.max(1, Math.round(60 / this.hoverFps));
    this.stepTimer++;

    if (this.spotlightIntensity <= 0.01 || !this.hoverStopMotion) {
      // Calm unlit idle state: smooth continuous drift & rotation
      this.rotation += this.rotationSpeed;
      this.renderRotation = this.rotation;
      this.renderFloatX = 0;
      this.renderFloatY = 0;
      this.renderJitterX = 0;
      this.renderJitterY = 0;
      this.renderJitterRot = 0;
    } else {
      // On hover under spotlight: step-motion quantized frame execution
      if (this.stepTimer >= stepInterval) {
        this.stepTimer = 0;
        this.stepCount++;

        // Step rotation increment on the stop-motion tick
        const rotStep = (this.rotationSpeed + this.hoverRotationSpeed) * stepInterval;
        this.rotation += rotStep;

        // Quantized sine-wave floating oscillation on hover
        if (this.hoverFloating) {
          const floatPhase = this.stepCount * 0.45;
          const floatY = Math.sin(floatPhase) * this.hoverFloatAmplitude * this.spotlightIntensity;
          const floatX = Math.cos(floatPhase * 0.7) * (this.hoverFloatAmplitude * 0.4) * this.spotlightIntensity;

          // Micro analog paper/cutout jitter on step beat
          const jAmp = this.hoverJitter * this.spotlightIntensity;
          this.renderJitterX = (Math.random() - 0.5) * jAmp;
          this.renderJitterY = (Math.random() - 0.5) * jAmp;
          this.renderJitterRot = (Math.random() - 0.5) * (jAmp * 0.02);

          this.renderFloatX = Math.round(floatX + this.renderJitterX);
          this.renderFloatY = Math.round(floatY + this.renderJitterY);
        } else {
          this.renderFloatX = 0;
          this.renderFloatY = 0;
        }

        this.renderRotation = this.rotation + this.renderJitterRot;
        this.tick += 1;
      }
    }
  }

  draw(ctx, config, parallaxX = 0, parallaxY = 0, layout = null, blurCanvas = null) {
    let cfg = config;
    let pX = parallaxX;
    let pY = parallaxY;

    if (config && (config instanceof HTMLCanvasElement || config.getContext)) {
      cfg = arguments[2] || {};
      pX = arguments[3] || 0;
      pY = arguments[4] || 0;
    }

    pX = typeof pX === "number" && !isNaN(pX) ? pX : 0;
    pY = typeof pY === "number" && !isNaN(pY) ? pY : 0;

    const basePosX = typeof this.worldX === "number" && !isNaN(this.worldX) ? this.worldX : 0;
    const basePosY = typeof this.worldY === "number" && !isNaN(this.worldY) ? this.worldY : 0;
    const floatX = this.renderFloatX || 0;
    const floatY = this.renderFloatY || 0;

    const drawX = basePosX + pX * (this.depth || 0) + floatX;
    const drawY = basePosY + pY * (this.depth || 0) + floatY;
    const currentRotation = this.renderRotation || this.rotation || 0;
    const itemOpacity = this.opacity !== undefined ? this.opacity : 1.0;
    if (itemOpacity <= 0.001) return;

    // Scale with optional subtle hover pulse
    const baseScale = this.size / Math.max(this.viewBoxWidth, this.viewBoxHeight);
    const scalePulse = 1.0 + (this.hoverScale - 1.0) * (this.spotlightIntensity || 0);
    const scale = baseScale * scalePulse;

    // -------------------------------------------------------------
    // LAYER 1: Solid Color Rotating Vector
    // -------------------------------------------------------------
    const solidAlpha = Math.max(0, (1 - (this.spotlightIntensity || 0)) * itemOpacity);
    if (solidAlpha > 0.005) {
      ctx.save();
      ctx.translate(drawX, drawY);
      ctx.rotate(currentRotation);
      ctx.scale(scale, scale);
      ctx.translate(-this.viewBoxWidth * 0.5, -this.viewBoxHeight * 0.5);

      ctx.fillStyle = this.color;
      ctx.globalAlpha = ctx.globalAlpha * solidAlpha;
      ctx.fill(this.path2d, "evenodd");
      ctx.restore();
    }

    // -------------------------------------------------------------
    // LAYER 2: ASCII Typography Matrix (Clipped to SVG Path)
    // -------------------------------------------------------------
    const asciiAlpha = Math.max(0, (this.spotlightIntensity || 0) * itemOpacity);
    if (asciiAlpha > 0.005) {
      ctx.save();
      ctx.translate(drawX, drawY);
      ctx.rotate(currentRotation);
      ctx.scale(scale, scale);
      ctx.translate(-this.viewBoxWidth * 0.5, -this.viewBoxHeight * 0.5);

      // Clip rendering to the exact SVG path boundary
      ctx.clip(this.path2d, "evenodd");

      ctx.font = `${this.fontWeight} ${this.fontSize}px ${this.fontFamily}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const [r, g, b] = this.asciiColorRGB;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${asciiAlpha})`;

      const stepX = this.charWidth || this.charSpacing || 14;
      const stepY = this.lineHeight || this.charSpacing || 16;
      const chars = this.asciiChars;
      const charCount = chars.length;
      const tickInt = Math.floor(this.tick || 0);

      // Render kinetic ASCII typography filling the clipped shape
      for (let py = stepY * 0.5; py < this.viewBoxHeight; py += stepY) {
        for (let px = stepX * 0.5; px < this.viewBoxWidth; px += stepX) {
          const charIdx = (Math.floor(px * 3.7 + py * 7.3) + tickInt) % charCount;
          ctx.fillText(chars[charIdx], px, py);
        }
      }
      ctx.restore();
    }
  }
}
